'use client'

import { useRef } from 'react'

import { mapManager, type Map } from './map-manager'

const RESIZE_THRESHOLD = 200

export const useResizingMap = () => {
  const resizingRef = useRef(false)
  const previousBoundsRef = useRef<naver.maps.Bounds | null>(null)
  const previousZoomLevelRef = useRef(0)
  const previousWidthRef = useRef(0)
  const programmaticZoomChangedRef = useRef(false)

  const isResizing = () => resizingRef.current

  const previousBounds = () => previousBoundsRef.current

  const previousZoomLevel = () => previousZoomLevelRef.current

  const previousWidth = () => previousWidthRef.current

  const isProgrammaticZoomChanged = () => programmaticZoomChangedRef.current

  const updateResizingStatus = (isResizing: boolean) => {
    resizingRef.current = isResizing
  }

  const updateProgrammaticZoomChanged = (isProgrammaticZoomChange: boolean) => {
    programmaticZoomChangedRef.current = isProgrammaticZoomChange
  }

  const keepCurrentBounds = (bounds: naver.maps.Bounds) => {
    previousBoundsRef.current = bounds
  }

  const keepCurrentZoomLevel = (zoomLevel: number) => {
    previousZoomLevelRef.current = zoomLevel
  }

  const keepCurrentWidth = (width: number) => {
    previousWidthRef.current = width
  }

  const keepCurrentMapState = (map: Map) => {
    const bounds = map.getBounds()
    const zoomLevel = map.getZoom()
    const width = map.getElement().clientWidth

    keepCurrentBounds(bounds)
    keepCurrentZoomLevel(zoomLevel)
    keepCurrentWidth(width)
  }

  const handleResizeMap = () => {
    mapManager.withMap(map => {
      const currentWidth = map.getElement().clientWidth

      if (currentWidth < previousWidth()) {
        resizingRef.current = true
      }

      if (!resizingRef.current) return

      const zoomLevel = map.getZoom()

      if (
        currentWidth < previousWidth() - RESIZE_THRESHOLD &&
        zoomLevel <= previousZoomLevel()
      ) {
        programmaticZoomChangedRef.current = true

        map.panToBounds(previousBounds()!)

        return
      }

      if (
        currentWidth >= previousWidth() - RESIZE_THRESHOLD &&
        zoomLevel < previousZoomLevel()
      ) {
        programmaticZoomChangedRef.current = true

        map.setZoom(previousZoomLevel(), true)
        updateResizingStatus(false)
      }
    })
  }

  return {
    isResizing,
    previousZoomLevel,
    isProgrammaticZoomChanged,
    updateResizingStatus,
    updateProgrammaticZoomChanged,
    keepCurrentMapState,
    handleResizeMap,
  }
}
